/**
 * Enhanced SEO configuration and utilities
 * Provides comprehensive SEO data for all pages and content types
 */

import { businessInfo } from './contentConfig';

export interface PageSEO {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogImage?: string;
  type?: 'website' | 'article' | 'product';
  schema?: any;
}

// Base SEO configuration
export const baseSEO = {
  siteName: businessInfo.company.name,
  baseUrl: 'https://chateaudeclementine.com',
  defaultImage: '/hero-castles.webp',
  locale: 'fr_BE',
  charset: 'UTF-8'
};

// Page-specific SEO configurations
export const pageSEO: Record<string, PageSEO> = {
  home: {
    title: `${businessInfo.company.name} - Location Châteaux Gonflables & Sumos en Belgique`,
    description: `Location de châteaux gonflables et sumos à ${businessInfo.location.address.city} depuis ${businessInfo.company.founded}. Prix à partir de 30€ pour le week-end complet. Retrait facile, montage simple en 10min.`,
    keywords: [
      'château gonflable', 'location château', 'sumo gonflable', 'anniversaire enfant',
      'fête enfant', 'Rhode-Saint-Genèse', 'Belgique', 'Bruxelles', 'Brabant Wallon',
      'animation enfant', 'jeux gonflables', 'location matériel fête'
    ],
    canonicalUrl: baseSEO.baseUrl,
    ogImage: baseSEO.defaultImage,
    type: 'website'
  },
  
  chateaux: {
    title: 'Châteaux Gonflables - Location à partir de 30€ | Château de Clémentine',
    description: 'Découvrez notre collection de châteaux gonflables : Princesse, Dino, Disco et plus. Location week-end complet avec matériel inclus. Montage facile en 10 minutes.',
    keywords: [
      'château gonflable location', 'château princesse', 'château dinosaure',
      'jeux gonflables enfants', 'animation anniversaire', 'Rhode-Saint-Genèse'
    ],
    canonicalUrl: `${baseSEO.baseUrl}/chateaux`,
    ogImage: '/hero-castles.webp',
    type: 'website'
  },

  sumos: {
    title: 'Sumos Gonflables - Animation Unique pour Vos Événements',
    description: 'Location de sumos gonflables pour adolescents et adultes. Animation originale pour mariages, événements d\'entreprise et fêtes. Matériel complet inclus.',
    keywords: [
      'sumo gonflable', 'animation sumo', 'location sumo', 'événement entreprise',
      'mariage animation', 'team building', 'Rhode-Saint-Genèse'
    ],
    canonicalUrl: `${baseSEO.baseUrl}/sumos`,
    ogImage: '/sumo-wrestling.jpg',
    type: 'website'
  },

  blog: {
    title: 'Blog - Conseils et Idées pour Vos Fêtes | Château de Clémentine',
    description: 'Découvrez nos conseils d\'expert pour organiser des fêtes inoubliables avec nos châteaux gonflables et animations. Guides pratiques et idées créatives.',
    keywords: [
      'conseils fête enfant', 'organisation anniversaire', 'jeux enfants',
      'animation fête', 'château gonflable guide', 'fête réussie'
    ],
    canonicalUrl: `${baseSEO.baseUrl}/blog`,
    type: 'website'
  },

  faq: {
    title: 'FAQ - Questions Fréquentes | Château de Clémentine',
    description: 'Trouvez les réponses à toutes vos questions sur la location de châteaux gonflables et sumos. Prix, livraison, montage, sécurité et plus.',
    keywords: [
      'faq château gonflable', 'questions location', 'prix château gonflable',
      'livraison château', 'montage château gonflable', 'sécurité'
    ],
    canonicalUrl: `${baseSEO.baseUrl}/faq`,
    type: 'website'
  }
};

// Generate structured data for different content types
export const generateStructuredData = {
  organization: () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": businessInfo.company.name,
    "description": pageSEO.home.description,
    "url": baseSEO.baseUrl,
    "email": businessInfo.company.email,
    "foundingDate": businessInfo.company.founded.toString(),
    "address": {
      "@type": "PostalAddress",
      "streetAddress": businessInfo.location.address.street,
      "addressLocality": businessInfo.location.address.city,
      "postalCode": businessInfo.location.address.postalCode,
      "addressCountry": businessInfo.location.address.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "50.7404",
      "longitude": "4.3037"
    },
    "openingHours": "Mo-Su 09:00-18:00",
    "priceRange": "30€-120€",
    "serviceArea": businessInfo.serviceAreas.map(area => ({
      "@type": "Place",
      "name": area
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Châteaux Gonflables et Sumos",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Location Château Gonflable",
            "description": "Location de châteaux gonflables pour fêtes d'enfants et événements"
          },
          "price": "50",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Location Sumos Gonflables",
            "description": "Location de costumes sumos gonflables pour animations"
          },
          "price": "50",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock"
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5"
    }
  }),

  breadcrumb: (items: Array<{ name: string; url: string }>) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  }),

  article: (article: any) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.subtitle || article.meta_description,
    "author": {
      "@type": "Organization",
      "name": businessInfo.company.name
    },
    "publisher": {
      "@type": "Organization",
      "name": businessInfo.company.name,
      "logo": {
        "@type": "ImageObject",
        "url": `${baseSEO.baseUrl}/logo.webp`
      }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "image": article.featured_image ? `${baseSEO.baseUrl}${article.featured_image}` : baseSEO.defaultImage,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseSEO.baseUrl}/blog/${article.slug}`
    }
  }),

  product: (chateau: any) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": chateau.nom,
    "description": chateau.description || `Château gonflable ${chateau.nom} - ${chateau.taille}`,
    "image": chateau.image_urls?.[0] || baseSEO.defaultImage,
    "brand": {
      "@type": "Brand",
      "name": businessInfo.company.name
    },
    "offers": {
      "@type": "Offer",
      "price": chateau.prix?.replace(/[^\d]/g, ''),
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": businessInfo.company.name
      }
    },
    "category": "Château Gonflable",
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Taille",
        "value": chateau.taille
      },
      {
        "@type": "PropertyValue", 
        "name": "Couleur",
        "value": chateau.couleur
      }
    ]
  })
};

// SEO validation utilities
export const validateSEO = {
  title: (title: string) => ({
    isValid: title.length >= 30 && title.length <= 60,
    length: title.length,
    recommendations: title.length < 30 ? 'Title trop court (min 30 caractères)' : 
                    title.length > 60 ? 'Title trop long (max 60 caractères)' : 'OK'
  }),
  
  description: (description: string) => ({
    isValid: description.length >= 120 && description.length <= 160,
    length: description.length,
    recommendations: description.length < 120 ? 'Description trop courte (min 120 caractères)' :
                    description.length > 160 ? 'Description trop longue (max 160 caractères)' : 'OK'
  }),
  
  keywords: (keywords: string[]) => ({
    isValid: keywords.length >= 5 && keywords.length <= 10,
    count: keywords.length,
    recommendations: keywords.length < 5 ? 'Ajoutez plus de mots-clés (min 5)' :
                    keywords.length > 10 ? 'Réduisez le nombre de mots-clés (max 10)' : 'OK'
  })
};